import { STORAGE_FLAG } from "../shared/constant";
import { BannerElements, ConsentGroup, ConsentState, DialogElements, ShoelaceDialog } from "../types/cookie-consent-type";


// Reads the embedded JSON config and returns the list of consent groups, logging parsing issues.
const parseGroups = (): ConsentGroup[] => {
  const script = document.getElementById("cookie-consent-groups") as HTMLScriptElement | null;
  if (!script?.textContent) {
    return [];
  }

  try {
    return JSON.parse(script.textContent) as ConsentGroup[];
  } catch (error) {
    console.error("Unable to parse cookie consent group configuration", error);
    return [];
  }
};

// Retrieves and parses the consent cookie payload; returns null if not present or malformed.
const readCookie = (cookieName: string): ConsentState | null => {
  const target = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${cookieName}=`));

  if (!target) {
    return null;
  }

  try {
    const value = decodeURIComponent(target.split("=")[1] ?? "");
    return JSON.parse(value) as ConsentState;
  } catch (error) {
    console.warn("Failed to parse cookie consent value", error);
    return null;
  }
};

// Serialises the consent map and writes it to document.cookie with the supplied directives.
const writeCookie = (
  name: string,
  value: ConsentState,
  options: {
    maxAge: number;
    path: string;
    sameSite: string;
    secure: boolean;
    domain: string | null;
  }
): void => {
  const encoded = encodeURIComponent(JSON.stringify(value));
  const directives: string[] = [
    `${name}=${encoded}`,
    `Max-Age=${Math.max(options.maxAge, 0)}`,
    `Path=${options.path}`,
    `SameSite=${options.sameSite}`,
  ];

  if (options.secure) {
    directives.push("Secure");
  }

  if (options.domain) {
    directives.push(`Domain=${options.domain}`);
  }

  document.cookie = directives.join("; ");
};

// Expires a specific cookie immediately, optionally targeting a given domain variant.
const deleteCookie = (name: string, domain?: string | null): void => {
  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  const directives = [`${name}=`, `expires=${expires}`, "path=/"];

  if (domain) {
    directives.push(`domain=${domain}`);
  }

  document.cookie = directives.join("; ");
};

// Removes any browser cookies that match the patterns declared for a consent group when revoked.
const deleteCookiesForGroup = (group: ConsentGroup): void => {
  if (group.cookies.length === 0) {
    return;
  }

  const cookieNames = document.cookie.split(";").map((entry) => {
    const [name] = entry.split("=");
    return name?.trim() ?? "";
  });

  cookieNames.forEach((name) => {
    if (!name) {
      return;
    }

    const shouldDelete = group.cookies.some((pattern) => {
      if (pattern.endsWith("*")) {
        return name.startsWith(pattern.slice(0, -1));
      }

      return name === pattern;
    });

    if (shouldDelete) {
      deleteCookie(name);

      const hostSegments = window.location.hostname.split(".");
      const isIpAddress = hostSegments.every((segment) => Number.isFinite(Number.parseFloat(segment)));

      if (!isIpAddress && hostSegments.length >= 2) {
        const baseDomain = `.${hostSegments.slice(-2).join(".")}`;
        deleteCookie(name, baseDomain);
      }

      if (window.location.hostname.startsWith("www.")) {
        const bare = window.location.hostname.replace(/^www\./, "");
        deleteCookie(name, bare);
        deleteCookie(name, `.${bare}`);
      }
    }
  });
};

// Ensures required groups remain enabled by merging the current state with mandatory allowances.
const ensureRequiredGroups = (consent: ConsentState, groups: ConsentGroup[]): ConsentState => {
  const nextState: ConsentState = { ...consent };

  groups.forEach((group) => {
    if (group.is_required) {
      nextState[group.varname] = true;
    }
  });

  return nextState;
};

// Grabs the banner container and its key buttons; returns null if any element is missing.
const getBannerElements = (): BannerElements | null => {
  const banner = document.getElementById("cookie-consent-banner") as HTMLDivElement | null;
  if (!banner) {
    return null;
  }

  const acceptAll = banner.querySelector<HTMLButtonElement>(".cookie-consent-banner__button--accept");
  const rejectAll = banner.querySelector<HTMLButtonElement>(".cookie-consent-banner__button--reject");
  const manage = banner.querySelector<HTMLButtonElement>(".cookie-consent-banner__button--manage");

  if (!acceptAll || !rejectAll || !manage) {
    return null;
  }

  return {
    banner,
    acceptAll,
    rejectAll,
    manage,
  };
};

// Collects the dialog, form, and footer controls for later interaction wiring.
const getDialogElements = (): DialogElements | null => {
  const dialog = document.getElementById("cookie-consent-dialog") as ShoelaceDialog | null;
  const form = document.getElementById("cookie-consent-form") as HTMLFormElement | null;
  const save = document.getElementById("cookie-consent-save") as HTMLButtonElement | null;
  const cancel = document.getElementById("cookie-consent-cancel") as HTMLButtonElement | null;

  if (!dialog || !form || !save || !cancel) {
    return null;
  }

  return {
    dialog,
    form,
    save,
    cancel,
  };
};

// Toggles the banner visibility class and aria-hidden flag to show or hide the prompt.
const setBannerVisibility = (banner: HTMLDivElement, visible: boolean): void => {
  if (visible) {
    banner.classList.add("is-visible");
    banner.setAttribute("aria-hidden", "false");
  } else {
    banner.classList.remove("is-visible");
    banner.setAttribute("aria-hidden", "true");
  }
};

// Flags in localStorage that the user has interacted with the consent UI.
const markSeen = (): void => {
  try {
    localStorage.setItem(STORAGE_FLAG, "true");
  } catch (error) {
    if (error instanceof Error) {
      console.warn(error.message);
    }
  }
};

// Reads cookie configuration options from data attributes on the banner element.
const parseBannerOptions = (banner: HTMLDivElement) => {
  const cookieName = banner.dataset.cookieName ?? "cookie_consent";
  const maxAge = Number.parseInt(banner.dataset.cookieMaxAge ?? "31536000", 10);
  const path = banner.dataset.cookiePath ?? "/";
  const sameSite = banner.dataset.cookieSamesite ?? "Lax";
  const secure = banner.dataset.cookieSecure !== "false";
  const domain = banner.dataset.cookieDomain ?? null;

  return {
    cookieName,
    maxAge: Number.isFinite(maxAge) ? maxAge : 31536000,
    path,
    sameSite,
    secure,
    domain,
  };
};

// Pre-fills the dialog's radio inputs to mirror the currently stored consent selections.
const syncDialogChoices = (
  dialog: ShoelaceDialog,
  groups: ConsentGroup[],
  consent: ConsentState
): void => {
  groups.forEach((group) => {
    if (group.is_required) {
      return;
    }

    const chooser = dialog.querySelector<HTMLDivElement>(
      `[data-group-choice="${group.varname}"]`
    );

    if (!chooser) {
      return;
    }

    const allow = chooser.querySelector<HTMLInputElement>('input[value="allow"]');
    const deny = chooser.querySelector<HTMLInputElement>('input[value="deny"]');

    if (allow && deny) {
      const shouldAllow = consent[group.varname] !== false;
      allow.checked = shouldAllow;
      deny.checked = !shouldAllow;
    }
  });
};

// Extracts the dialog selections into a new consent state, defaulting to previous choices.
const collectDialogConsent = (
  dialog: ShoelaceDialog,
  groups: ConsentGroup[],
  previous: ConsentState
): ConsentState => {
  const next: ConsentState = { ...previous };

  groups.forEach((group) => {
    if (group.is_required) {
      next[group.varname] = true;
      return;
    }

    const chooser = dialog.querySelector<HTMLDivElement>(
      `[data-group-choice="${group.varname}"]`
    );

    if (!chooser) {
      next[group.varname] = previous[group.varname] ?? true;
      return;
    }

    const selected = chooser.querySelector<HTMLInputElement>('input[type="radio"]:checked');

    if (!selected) {
      next[group.varname] = previous[group.varname] ?? true;
      return;
    }

    next[group.varname] = selected.value !== "deny";
  });

  return next;
};

// Schedules a short-delayed page reload so scripts respond to updated consent settings.
const reloadAfterDelay = (): void => {
  window.setTimeout(() => {
    window.location.reload();
  }, 150);
};

// Bootstraps the entire consent workflow, wiring banner actions, dialog events, and persistence.
const initialiseConsent = () => {
  const groups = parseGroups();
  const bannerElements = getBannerElements();

  if (!bannerElements) {
    return;
  }

  const { banner, acceptAll, rejectAll, manage } = bannerElements;
  const dialogElements = getDialogElements();
  const options = parseBannerOptions(banner);
  const getStoredConsent = () => readCookie(options.cookieName);
  const getEffectiveConsent = () => ensureRequiredGroups(getStoredConsent() ?? {}, groups);
  const storedConsent = getStoredConsent();

  if (storedConsent) {
    setBannerVisibility(banner, false);
  } else {
    setBannerVisibility(banner, true);
  }

  const saveConsent = (consent: ConsentState, reload = false): void => {
    const ensured = ensureRequiredGroups(consent, groups);
    writeCookie(options.cookieName, ensured, {
      maxAge: options.maxAge,
      path: options.path,
      sameSite: options.sameSite,
      secure: options.secure,
      domain: options.domain,
    });
    markSeen();
    setBannerVisibility(banner, false);

    if (reload) {
      reloadAfterDelay();
    }
  };

  acceptAll.addEventListener("click", () => {
    const consent: ConsentState = {};
    groups.forEach((group) => {
      consent[group.varname] = true;
    });
    saveConsent(consent, true);
  });

  rejectAll.addEventListener("click", () => {
    const previous = getEffectiveConsent();
    const consent: ConsentState = {};
    groups.forEach((group) => {
      consent[group.varname] = group.is_required;
      if (!group.is_required && previous[group.varname]) {
        deleteCookiesForGroup(group);
      }
    });
    saveConsent(consent, true);
  });

  if (dialogElements) {
    const { dialog, save, cancel, form } = dialogElements;

    const openDialog = () => {
      if (typeof dialog.show === "function") {
        void dialog.show();
      } else {
        dialog.setAttribute("open", "");
      }
    };

    const closeDialog = () => {
      if (typeof dialog.hide === "function") {
        void dialog.hide();
      } else {
        dialog.removeAttribute("open");
      }
    };

    const handleSave = () => {
      const previous = getEffectiveConsent();
      const consent = collectDialogConsent(dialog, groups, previous);

      groups.forEach((group) => {
        if (group.is_required) {
          return;
        }

        if (previous[group.varname] && !consent[group.varname]) {
          deleteCookiesForGroup(group);
        }
      });

      closeDialog();
      saveConsent(consent, true);
    };

    manage.addEventListener("click", (event) => {
      event.preventDefault();
      syncDialogChoices(dialog, groups, getEffectiveConsent());
      openDialog();
    });

    cancel.addEventListener("click", (event) => {
      event.preventDefault();
      closeDialog();
    });

    save.addEventListener("click", (event) => {
      event.preventDefault();
      handleSave();
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      handleSave();
    });
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initialiseConsent, { once: true });
} else {
  initialiseConsent();
}
